import React from 'react';
import { Command } from 'cmdk';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SECTIONS, NAV_ITEMS } from '../../config/nav-config';
import { useCapabilities } from '../../auth/CapabilitiesContext';
import { useGoogleSheetsConfig } from '../../hooks/useGoogleSheetsConfig';
import { HIDDEN_MENU_PATHS } from '../../config';
import NavIcon from '../ui/NavIcon';
import { useCommandPalette } from './useCommandPalette';

export default function CommandPalette() {
  const { t } = useTranslation(['menu', 'ui']);
  const { can } = useCapabilities();
  const navigate = useNavigate();
  const { open, setOpen } = useCommandPalette();
  const { enabled: googleSheetsEnabled, spreadsheetUrl: googleSheetsUrl } = useGoogleSheetsConfig();

  const ctx = { googleSheetsEnabled, googleSheetsUrl };

  const isAllowed = (item) => {
    if (HIDDEN_MENU_PATHS.includes(item.path)) return false;
    // Mesmo predicado do Sidebar e do RequireCapability.
    return can(item.capability);
  };

  // 40-item filter is negligible — no memoization needed.
  const groups = SECTIONS.map((section) => ({
    ...section,
    items: NAV_ITEMS.filter(
      (i) => i.section === section.id && isAllowed(i) && (!i.conditional || ctx[i.conditional]),
    ),
  })).filter((g) => g.items.length > 0);

  const handleSelect = (item) => {
    setOpen(false);
    if (item.external) {
      const href = item.hrefFrom ? ctx[item.hrefFrom] : item.path;
      if (href) window.open(href, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(item.path);
  };

  // Rely on Radix Dialog's built-in outside-click / Esc close behavior
  // (cmdk's Command.Dialog wraps Radix Dialog). No custom backdrop click handler needed.
  return (
    <Command.Dialog
      open={open}
      onOpenChange={setOpen}
      label={t('ui:commandPalette.label')}
      className="fixed inset-0 z-[100] bg-gray-900/50 flex items-start justify-center pt-[15vh]"
    >
      <div className="relative w-full max-w-xl mx-4 bg-white rounded-lg shadow-xl overflow-hidden">
        <Command.Input
          placeholder={t('ui:commandPalette.placeholder')}
          className="w-full px-4 py-3 text-sm border-b border-gray-200 focus:outline-none"
        />
        <Command.List className="max-h-[60vh] overflow-y-auto p-2">
          <Command.Empty className="py-6 text-center text-sm text-gray-500">
            {t('ui:commandPalette.empty')}
          </Command.Empty>
          {groups.map((group) => (
            <Command.Group
              key={group.id}
              heading={t(group.labelKey)}
              className="text-xs font-semibold uppercase tracking-wider text-gray-500 px-2 py-1"
            >
              {group.items.map((item) => (
                <Command.Item
                  key={item.path}
                  value={`${t(item.labelKey)} ${item.path}`}
                  onSelect={() => handleSelect(item)}
                  className="flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer text-sm text-gray-700 aria-selected:bg-primary-100 aria-selected:text-primary-900"
                >
                  <NavIcon name={item.iconName} className="h-4 w-4 text-gray-400" />
                  <span className="flex-1">{t(item.labelKey)}</span>
                  <span className="text-xs text-gray-400" aria-hidden="true">{item.path}</span>
                </Command.Item>
              ))}
            </Command.Group>
          ))}
        </Command.List>
      </div>
    </Command.Dialog>
  );
}
